import { Button } from '@workspace/ui/components/button';
import { Input } from '@workspace/ui/components/input';
import { Label } from '@workspace/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select';
import { cn } from '@workspace/ui/lib/utils';
import { SendHorizonal } from 'lucide-react';
import { useState } from 'react';

import { PhoneInput } from '~/components/phone-input';
import type { FormControl } from '~/hooks/use-chat/types';

import { fetchJson } from '~/utils/fetch-json-v2';
import type { ToolProps } from './types';

export function DynamicForm({ handleAction, toolCall, isActive }: ToolProps) {
  // biome-ignore lint/suspicious/noExplicitAny: toolCall args structure varies
  const args = toolCall.args as any;
  const [formData, setFormData] = useState<
    Record<string, string | boolean | File>
  >({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useState(() => {
    if (toolCall.name !== 'human_input') {
      return;
    }

    const initialData: Record<string, string> = {};

    // biome-ignore lint/suspicious/noExplicitAny: form_controls structure varies
    args.form_controls.forEach((control: any) => {
      if (control.value && control.type !== 'submit') {
        initialData[control.form_name] = control.value;
      }
    });
    setFormData(initialData);
  });

  if (toolCall.name !== 'human_input') {
    return null;
  }

  const handleInputChange = (name: string, value: string | boolean | File) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // biome-ignore lint/suspicious/noExplicitAny: form_controls structure varies
    args.form_controls.forEach((control: any) => {
      if (control.required && control.type !== 'submit') {
        const value = formData[control.form_name];
        if (control.type === 'checkbox') {
          if (!value) {
            newErrors[control.form_name] =
              `${control.placeholder} es requerido`;
          }
        } else if (control.type === 'file') {
          if (!value || !(value instanceof File)) {
            newErrors[control.form_name] =
              `${control.label || control.placeholder} es requerido`;
          }
        } else {
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            newErrors[control.form_name] =
              `${control.label || control.placeholder} es requerido`;
          }
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const processedData: Record<string, string | boolean> = {};

      for (const [key, value] of Object.entries(formData)) {
        if (value instanceof File) {
          const formData = new FormData();
          formData.append('file', value);

          const { data: fileData } = await fetchJson<{ urn: string }>(
            '/files',
            {
              method: 'POST',
              body: formData,
            },
          );

          processedData[key] = fileData?.urn || '';
        } else {
          processedData[key] = value;
        }
      }

      await handleAction(toolCall, JSON.stringify(processedData));
    } catch (error) {
      console.error('Error al enviar formulario:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFormControl = (control: FormControl) => {
    const hasError = errors[control.form_name];

    switch (control.type) {
      case 'text':
        return (
          <div key={control.form_name} className="space-y-2">
            <Label htmlFor={control.form_name}>
              {control.label}
              {control.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={control.form_name}
              name={control.form_name}
              type="text"
              placeholder={control.placeholder}
              value={(formData[control.form_name] as string) || ''}
              onChange={(e) =>
                handleInputChange(control.form_name, e.target.value)
              }
              className={cn('dark:bg-background', {
                'border-red-500': hasError,
              })}
              required={control.required}
              disabled={!isActive}
            />
            {hasError && <p className="text-sm text-red-500">{hasError}</p>}
          </div>
        );

      case 'number':
        return (
          <div key={control.form_name} className="space-y-2">
            <Label htmlFor={control.form_name}>
              {control.label}
              {control.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={control.form_name}
              name={control.form_name}
              type="number"
              placeholder={control.placeholder}
              value={(formData[control.form_name] as string) || ''}
              onChange={(e) =>
                handleInputChange(control.form_name, e.target.value)
              }
              className={cn('dark:bg-background', {
                'border-red-500': hasError,
              })}
              required={control.required}
              disabled={!isActive}
            />
            {hasError && <p className="text-sm text-red-500">{hasError}</p>}
          </div>
        );

      case 'select':
        return (
          <div key={control.form_name} className="space-y-2">
            <Label htmlFor={control.form_name}>
              {control.label}
              {control.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Select
              value={(formData[control.form_name] as string) || ''}
              onValueChange={(value) =>
                handleInputChange(control.form_name, value)
              }
              required={control.required}
            >
              <SelectTrigger
                className={cn(
                  'w-full dark:bg-background border-[#27272a] text-white text-sm h-8',
                  {
                    'border-red-500': hasError,
                  },
                )}
                disabled={!isActive}
              >
                <SelectValue placeholder={control.placeholder} />
              </SelectTrigger>
              <SelectContent className="bg-background border-gray-700">
                {control.options?.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    className="text-white hover:bg-gray-800"
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasError && <p className="text-sm text-red-500">{hasError}</p>}
          </div>
        );

      case 'date':
        return (
          <div key={control.form_name} className="space-y-2">
            <Label htmlFor={control.form_name}>
              {control.placeholder}
              {control.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={control.form_name}
              name={control.form_name}
              type="date"
              value={(formData[control.form_name] as string) || ''}
              onChange={(e) =>
                handleInputChange(control.form_name, e.target.value)
              }
              className={cn('dark:bg-background', {
                'border-red-500': hasError,
              })}
              required={control.required}
              disabled={!isActive}
            />
            {hasError && <p className="text-sm text-red-500">{hasError}</p>}
          </div>
        );

      case 'checkbox':
        return (
          <div key={control.form_name} className="flex items-center space-x-2">
            <input
              id={control.form_name}
              name={control.form_name}
              type="checkbox"
              checked={(formData[control.form_name] as boolean) || false}
              onChange={(e) =>
                handleInputChange(control.form_name, e.target.checked)
              }
              className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${hasError ? 'border-red-500' : ''}`}
              required={control.required}
              disabled={!isActive}
            />
            <Label
              htmlFor={control.form_name}
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {control.label}
              {control.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            {hasError && (
              <p className="text-sm text-red-500 ml-6">{hasError}</p>
            )}
          </div>
        );

      case 'phone':
        return (
          <div key={control.form_name} className="space-y-2">
            <Label htmlFor={control.form_name}>
              {control.label}
              {control.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <PhoneInput
              name={control.form_name}
              placeholder={control.placeholder}
              required={control.required}
              value={(formData[control.form_name] as string) || ''}
              onChange={(value) => handleInputChange(control.form_name, value)}
              hasError={!!hasError}
            />
            {hasError && <p className="text-sm text-red-500">{hasError}</p>}
            <p className="text-xs text-muted-foreground">
              Formato final:{' '}
              {(formData[control.form_name] as string) || 'Código + número'}
            </p>
          </div>
        );

      case 'file':
        return (
          <div key={control.form_name} className="space-y-2">
            <Label htmlFor={control.form_name}>
              {control.label}
              {control.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={control.form_name}
              name={control.form_name}
              type="file"
              accept={control.accept}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleInputChange(control.form_name, file);
                }
              }}
              className={cn('dark:bg-background', {
                'border-red-500': hasError,
              })}
              required={control.required}
              disabled={!isActive}
            />
            {hasError && <p className="text-sm text-red-500">{hasError}</p>}
            {formData[control.form_name] instanceof File && (
              <p className="text-xs text-muted-foreground">
                Archivo seleccionado:{' '}
                {(formData[control.form_name] as File).name}
              </p>
            )}
          </div>
        );

      case 'submit':
        return (
          <div key={control.form_name} className="flex justify-end">
            <Button type="submit" disabled={isSubmitting || !isActive}>
              {isSubmitting
                ? 'Enviando...'
                : control.label || control.placeholder}
              <SendHorizonal className="h-4 w-4 ml-2" />
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-0">
      <div className="max-w-4xl mx-auto">
        {(args.description || args.why_human_input) && (
          <div className="mb-8">
            <h1 className="text-sm font-bold text-white mb-1">
              {args.description || args.why_human_input}
            </h1>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {args.form_controls.map(renderFormControl)}
        </form>
      </div>
    </div>
  );
}
